import { SOCIAL_NETWORKS, getSocialIcon } from './socialNetworks';
import { getIconApiUrl } from './iconUtils';

// Helper pour générer le style margin à partir des propriétés de l'élément
const getMarginStyle = (style) => {
    const marginTop = style?.marginTop || '0px';
    const marginRight = style?.marginRight || '0px';
    const marginBottom = style?.marginBottom || '0px';
    const marginLeft = style?.marginLeft || '0px';
    return `margin: ${marginTop} ${marginRight} ${marginBottom} ${marginLeft};`;
};

export const generateHTML = (grid, containerWidth = 600, options = {}) => {
    const isFreePlan = !options.subscriptionPlan || options.subscriptionPlan === 'free';

    let html = '<!DOCTYPE html>\n<html>\n<head>\n<meta charset="UTF-8">\n</head>\n<body>\n';
    html += `<table style="border-collapse: collapse; font-family: Arial, sans-serif; width: ${containerWidth}px; border: none;">\n`;

    grid.forEach(row => {
        html += '  <tr style="border: none;">\n';
        row.cells.forEach(cell => {
            const padding = cell.style?.padding || '15px';
            const valign = cell.style?.verticalAlign || 'top';
            const width = cell.style?.width || 'auto';
            html += `    <td style="padding: ${padding}; vertical-align: ${valign}; width: ${width}; border: none;">\n`;

            cell.elements?.forEach(element => {
                html += generateElementHTML(element);
            });

            if (cell.nestedGrid) {
                html += generateNestedHTML(cell.nestedGrid);
            }

            html += '    </td>\n';
        });
        html += '  </tr>\n';
    });

    if (isFreePlan) {
        const totalCols = grid[0]?.cells?.length || 1;
        html += '  <tr style="border: none;">\n';
        html += `    <td colspan="${totalCols}" style="border: none; padding: 8px 0 0 0; text-align: center; border-top: 1px solid #e5e7eb;">\n`;
        html += '      <a href="https://www.signly.fr" target="_blank" rel="noopener noreferrer" style="font-size: 11px; color: #9ca3af; text-decoration: none; font-family: Arial, sans-serif;">Propulsé par Signly</a>\n';
        html += '    </td>\n';
        html += '  </tr>\n';
    }

    html += '</table>\n</body>\n</html>';
    return html;
};

const generateNestedHTML = (nestedGrid) => {
    let html = '<table style="width: 100%; border-collapse: collapse; border: none;">\n';

    nestedGrid.forEach(row => {
        html += '  <tr style="border: none;">\n';
        row.cells?.forEach(cell => {
            const padding = cell.style?.padding || '10px';
            const valign = cell.style?.verticalAlign || 'top';
            html += `    <td style="padding: ${padding}; vertical-align: ${valign}; border: none;">\n`;

            cell.elements?.forEach(element => {
                html += generateElementHTML(element);
            });

            html += '    </td>\n';
        });
        html += '  </tr>\n';
    });

    html += '</table>\n';
    return html;
};

const generateElementHTML = (element) => {
    const style = element.style || {};

    switch (element.type) {
        case 'text':
            const textContent = element.value;
            const textLink = element.link;
            const textStyle = `color: ${style.color || '#000'}; font-size: ${style.fontSize || '14px'}; font-weight: ${style.fontWeight || 'normal'}; font-style: ${style.fontStyle || 'normal'}; text-decoration: ${style.textDecoration || 'none'};`;
            const textAlign = style.textAlign || 'left';

            if (textLink) {
                return `      <div style="${getMarginStyle(style)} text-align: ${textAlign};"><a href="${textLink}" style="${textStyle}">${textContent}</a></div>\n`;
            }
            return `      <div style="${getMarginStyle(style)} ${textStyle} text-align: ${textAlign};">${textContent}</div>\n`;

        case 'richtext':
            return `      <div style="${getMarginStyle(style)}">${element.value}</div>\n`;

        case 'image':
            const imgHeight = style.height && style.height !== '0' && style.height !== 'auto' ? style.height : 'auto';
            const imgStyle = `max-width: ${style.width || '200px'}; width: ${style.width || 'auto'}; height: ${imgHeight}; display: block; border: none;`;
            const imgAlign = style.textAlign || 'left';
            const imageElement = `<img src="${element.value}" style="${imgStyle}" alt="${element.alt || ''}" />`;

            if (element.link) {
                return `      <div style="${getMarginStyle(style)} text-align: ${imgAlign};"><a href="${element.link}">${imageElement}</a></div>\n`;
            }
            return `      <div style="${getMarginStyle(style)} text-align: ${imgAlign};">${imageElement}</div>\n`;

        case 'banner':
            const bannerLink = element.link || '#';
            return `      <a href="${bannerLink}" style="display: block; ${getMarginStyle(style)}"><img src="${element.value}" style="width: 100%; height: ${style.height || '150px'}; display: block; border: none;" alt="${element.alt || 'Banner'}" /></a>\n`;

        case 'button':
            return `      <div style="${getMarginStyle(style)}"><a href="${element.link || '#'}" style="display: inline-block; padding: ${style.padding || '12px 24px'}; background: ${style.backgroundColor || '#3498db'}; color: ${style.color || '#fff'}; text-decoration: none; border-radius: ${style.borderRadius || '6px'};">${element.value}</a></div>\n`;

        case 'icontext':
            const iconType = element.iconType || 'emoji';
            const iconSize = style.iconSize || '20px';
            const iconColor = style.iconColor || style.color || '#000000';
            let iconHtml;

            if (iconType === 'library' && element.libraryIcon) {
                // Icône de la bibliothèque - utiliser l'API pour générer l'icône colorée
                const iconSizeNum = parseInt(iconSize);
                const iconUrl = getIconApiUrl(element.libraryIcon, iconColor, iconSizeNum);
                iconHtml = `<img src="${iconUrl}" alt="${element.libraryIcon.name}" style="width: ${iconSize}; height: ${iconSize}; display: block; border: none;" />`;
            } else if (iconType === 'image') {
                // Image personnalisée
                iconHtml = `<img src="${element.iconUrl || ''}" alt="icon" style="width: ${iconSize}; height: ${iconSize}; display: block; border: none;" />`;
            } else {
                // Emoji
                iconHtml = `<span style="font-size: ${iconSize}; color: ${iconColor};">${element.icon || '📧'}</span>`;
            }

            const text = element.text || element.value;
            const content = element.link ? `<a href="${element.link}" style="color: inherit; text-decoration: none;">${text}</a>` : text;
            return `      <div style="${getMarginStyle(style)}"><table style="border-collapse: collapse; border: none;"><tr style="border: none;"><td style="border: none; padding: 0; vertical-align: middle;">${iconHtml}</td><td style="border: none; padding: 0 0 0 8px; vertical-align: middle; color: ${style.color || '#000'}; font-size: ${style.fontSize || '14px'};">${content}</td></tr></table></div>\n`;

        case 'divider':
            return `      <hr style="border: none; border-top: ${style.height || '1px'} solid ${style.color || '#e0e0e0'}; ${getMarginStyle(style)}" />\n`;

        case 'spacer':
            return `      <div style="height: ${style.height || '20px'};"></div>\n`;

        case 'social':
            const network = SOCIAL_NETWORKS[element.network] || SOCIAL_NETWORKS.linkedin;
            const iconUrl = getSocialIcon(element.network || 'linkedin', element.iconStyle || 'flat');
            return `      <div style="${getMarginStyle(style)}"><a href="${element.link || '#'}" target="_blank"><img src="${iconUrl}" style="width: ${style.size || '32px'}; height: ${style.size || '32px'}; display: block;" alt="${network.name}" /></a></div>\n`;

        case 'socialgroup':
            const networks = element.networks || [];
            if (networks.length === 0) return '';

            const spacing = parseInt(style.spacing || '8') / 2;
            const align = style.align || 'flex-start';
            const tableAlign = align === 'center' ? 'center' : align === 'flex-end' ? 'right' : 'left';

            let socialHtml = `      <div style="${getMarginStyle(style)}"><table style="border-collapse: collapse; border: none;" align="${tableAlign}">\n        <tr style="border: none;">\n`;

            networks.forEach((netKey, idx) => {
                const net = SOCIAL_NETWORKS[netKey] || SOCIAL_NETWORKS.linkedin;
                const netIconUrl = getSocialIcon(netKey, element.iconStyle || 'flat');
                const link = element.links?.[netKey] || '#';
                const paddingLeft = idx > 0 ? `${spacing}px` : '0';
                const paddingRight = idx < networks.length - 1 ? `${spacing}px` : '0';

                socialHtml += `          <td style="border: none; padding: 0 ${paddingRight} 0 ${paddingLeft};"><a href="${link}" target="_blank" rel="noopener noreferrer"><img src="${netIconUrl}" style="width: ${style.size || '32px'}; height: ${style.size || '32px'}; display: block; border: none;" alt="${net.name}" /></a></td>\n`;
            });

            socialHtml += '        </tr>\n      </table></div>\n';
            return socialHtml;

        case 'qrcode':
            const qrSize = style.size || '128';
            const qrData = encodeURIComponent(element.qrData || element.value || 'https://example.com');
            const qrFg = (style.fgColor || '#000000').replace('#', '');
            const qrBg = (style.bgColor || '#ffffff').replace('#', '');
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${qrData}&color=${qrFg}&bgcolor=${qrBg}`;
            return `      <div style="${getMarginStyle(style)} text-align: ${style.align || 'center'}; padding: ${style.margin || '0'};"><img src="${qrUrl}" style="width: ${qrSize}px; height: ${qrSize}px;" alt="QR Code" /></div>\n`;

        default:
            return `      <div style="${getMarginStyle(style)}">${element.value}</div>\n`;
    }
};
